import { FiniquitoForm } from '../components/labor/FiniquitoForm';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import {
  Calculator,
  ArrowLeft,
  Briefcase,
  DollarSign,
  Calendar as CalendarIcon,
  User,
  Building2,
  FileText,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  ChevronDown,
  Download,
  HelpCircle,
  Scale,
  Clock,
  TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Termination type configurations
const TERMINATION_CONFIGS = {
  despido_injustificado: { color: 'red', icon: AlertTriangle, surcharge: '30%' },
  despido_indirecto: { color: 'orange', icon: AlertTriangle, surcharge: '50%' },
  necesidades_empresa: { color: 'blue', icon: Building2, surcharge: null },
  desahucio: { color: 'purple', icon: FileText, surcharge: null },
  renuncia: { color: 'green', icon: User, surcharge: null },
  mutuo_acuerdo: { color: 'teal', icon: CheckCircle, surcharge: null },
  vencimiento_plazo: { color: 'gray', icon: CalendarIcon, surcharge: null },
  causal_disciplinaria: { color: 'slate', icon: Scale, surcharge: null }
};

// Currency formatter
const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const LaborCalculatorPage = () => {
  const { api } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: format(new Date(), 'yyyy-MM-dd'),
    base_salary: '',
    termination_type: 'despido_injustificado',
    contract_type: 'indefinido',
    bonuses: '',
    commissions: '',
    overtime_avg: '',
    vacation_days_taken: 0,
    notice_given: false,
    gratification_pending: false,
    annual_bonus: ''
  });
  
  // State
  const [terminationTypes, setTerminationTypes] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [legalLimits, setLegalLimits] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [result, setResult] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState('calculator'); // calculator, scenarios, info
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);
  
  const loadInitialData = async () => {
    try {
      const [typesRes, contractsRes, limitsRes, scenariosRes] = await Promise.all([
        api.get('/labor/termination-types'),
        api.get('/labor/contract-types'),
        api.get('/labor/legal-limits'),
        api.get('/labor/common-scenarios')
      ]);
      
      setTerminationTypes(typesRes.data.termination_types || []);
      setContractTypes(contractsRes.data.contract_types || []);
      setLegalLimits(limitsRes.data);
      setScenarios(scenariosRes.data.scenarios || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear result when form changes
    setResult(null);
  };
  
  const handleCalculate = async () => {
    // Validation
    if (!formData.start_date || !formData.base_salary) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        base_salary: parseFloat(formData.base_salary.toString().replace(/\D/g, '')) || 0,
        bonuses: parseFloat(formData.bonuses?.toString().replace(/\D/g, '')) || 0,
        commissions: parseFloat(formData.commissions?.toString().replace(/\D/g, '')) || 0,
        overtime_avg: parseFloat(formData.overtime_avg?.toString().replace(/\D/g, '')) || 0,
        annual_bonus: parseFloat(formData.annual_bonus?.toString().replace(/\D/g, '')) || 0,
        vacation_days_taken: parseInt(formData.vacation_days_taken) || 0
      };
      
      const response = await api.post('/labor/calculate', payload);
      setResult(response.data);
      setLastPayload(payload);
      toast.success('Cálculo completado');
    } catch (error) {
      console.error('Error calculating:', error);
      toast.error(error.response?.data?.detail || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };
  
  const formatSalaryInput = (value) => {
    const numericValue = value.replace(/\D/g, '');
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };
  
  if (loadingData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="labor-calculator-page">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Calculadora Laboral</h1>
                <p className="text-xs text-slate-400">Indemnizaciones y finiquitos chilenos</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
          <button
            onClick={() => setActiveTab('calculator')}
            data-testid="tab-calculator"
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'calculator'
                ? 'bg-emerald-500/20 text-emerald-400 border-b-2 border-emerald-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Calculator className="w-4 h-4" />
            Calculadora
          </button>
          <button
            onClick={() => setActiveTab('scenarios')}
            data-testid="tab-scenarios"
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'scenarios'
                ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4" />
            Escenarios Comunes
          </button>
          <button
            onClick={() => setActiveTab('info')}
            data-testid="tab-info"
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'info'
                ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Info className="w-4 h-4" />
            Información Legal
          </button>
        </div>
        
        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Form */}
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-400" />
                  Información Básica
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Fecha de Ingreso *</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        data-testid="input-start-date"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Fecha de Término *</label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        data-testid="input-end-date"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Sueldo Base Mensual *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="text"
                        value={formData.base_salary}
                        onChange={(e) => handleInputChange('base_salary', formatSalaryInput(e.target.value))}
                        placeholder="1.500.000"
                        data-testid="input-base-salary"
                        className="w-full pl-8 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Tipo de Despido *</label>
                    <select
                      value={formData.termination_type}
                      onChange={(e) => handleInputChange('termination_type', e.target.value)}
                      data-testid="select-termination-type"
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                    >
                      {terminationTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name} {type.surcharge ? `(+${type.surcharge})` : ''}
                        </option>
                      ))}
                    </select>
                    {terminationTypes.find(t => t.id === formData.termination_type)?.description && (
                      <p className="text-xs text-slate-500 mt-1">
                        {terminationTypes.find(t => t.id === formData.termination_type)?.description}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Tipo de Contrato</label>
                    <select
                      value={formData.contract_type}
                      onChange={(e) => handleInputChange('contract_type', e.target.value)}
                      data-testid="select-contract-type"
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                    >
                      {contractTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="notice_given"
                      checked={formData.notice_given}
                      onChange={(e) => handleInputChange('notice_given', e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500"
                    />
                    <label htmlFor="notice_given" className="text-sm text-slate-400">
                      Se dio aviso previo de 30 días
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="gratification_pending"
                      data-testid="input-gratification-pending"
                      checked={formData.gratification_pending}
                      onChange={(e) => handleInputChange('gratification_pending', e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500"
                    />
                    <label htmlFor="gratification_pending" className="text-sm text-slate-400">
                      La gratificación de este año no se pagó mensualmente
                    </label>
                  </div>
                </div>
              </div>
              
              {/* Advanced Options */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-800/50 transition-colors"
                >
                  <span className="font-medium text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    Opciones Avanzadas
                  </span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                </button>
                
                {showAdvanced && (
                  <div className="px-6 pb-6 space-y-4 border-t border-slate-700 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-1">Bonos Mensuales</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <input
                            type="text"
                            value={formData.bonuses}
                            onChange={(e) => handleInputChange('bonuses', formatSalaryInput(e.target.value))}
                            placeholder="0"
                            className="w-full pl-8 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-1">Comisiones Promedio</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <input
                            type="text"
                            value={formData.commissions}
                            onChange={(e) => handleInputChange('commissions', formatSalaryInput(e.target.value))}
                            placeholder="0"
                            className="w-full pl-8 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-1">Horas Extra Promedio</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <input
                            type="text"
                            value={formData.overtime_avg}
                            onChange={(e) => handleInputChange('overtime_avg', formatSalaryInput(e.target.value))}
                            placeholder="0"
                            className="w-full pl-8 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-1">Aguinaldo Anual</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <input
                            type="text"
                            value={formData.annual_bonus}
                            onChange={(e) => handleInputChange('annual_bonus', formatSalaryInput(e.target.value))}
                            placeholder="0"
                            className="w-full pl-8 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Días de Vacaciones ya tomados este año</label>
                      <input
                        type="number"
                        value={formData.vacation_days_taken}
                        onChange={(e) => handleInputChange('vacation_days_taken', e.target.value)}
                        min="0"
                        max="30"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                disabled={loading}
                data-testid="calculate-btn"
                className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5" />
                    Calcular Finiquito
                  </>
                )}
              </button>
            </div>
            
            {/* Right Column - Results */}
            <div className="space-y-4">
              {result ? (
                <>
                  {/* Summary Card */}
                  <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-emerald-100 text-sm">Total Finiquito Estimado</p>
                        <p data-testid="labor-total" className="text-4xl font-bold text-white">
                          {formatCLP(result.summary?.total_gross || 0)}
                        </p>
                      </div>
                      <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                        <DollarSign className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-white/10 rounded-lg p-3">
                        <p className="text-emerald-100">Tiempo Trabajado</p>
                        <p className="text-white font-medium">{result.employee_info?.service_time}</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3">
                        <p className="text-emerald-100">Base de Cálculo</p>
                        <p className="text-white font-medium">{formatCLP(result.salary_info?.average_salary_for_calculation || 0)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {result.indicators && (
                    <div data-testid="labor-indicators" className="flex flex-wrap gap-2 text-xs">
                      <span className="bg-slate-800 border border-slate-700 rounded-full px-3 py-1 text-slate-300">UF tope (Art. 172, {result.indicators.uf_art172.date}): {formatCLP(result.indicators.uf_art172.value)}</span>
                      <span className="bg-slate-800 border border-slate-700 rounded-full px-3 py-1 text-slate-300">UTM {result.indicators.utm.month}: {formatCLP(result.indicators.utm.value)}</span>
                      <span className="bg-slate-800 border border-slate-700 rounded-full px-3 py-1 text-slate-300">IMM: {formatCLP(result.indicators.imm.value)}</span>
                      <span className="text-slate-500 self-center">Fuente oficial: SII</span>
                    </div>
                  )}

                  {/* Breakdown */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-400" />
                      Desglose del Finiquito
                    </h4>
                    <div className="space-y-3">
                      {result.summary?.breakdown?.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div>
                            <p className="text-white font-medium">{item.concept}</p>
                            <p className="text-xs text-slate-400">{item.details}</p>
                          </div>
                          <p className="text-emerald-400 font-semibold">{formatCLP(item.amount)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Legal Notes */}
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Notas Legales
                    </h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      {result.legal_notes?.map((note, index) => (
                        <li key={index}>• {note}</li>
                      ))}
                    </ul>
                  </div>
                  {lastPayload && <FiniquitoForm payload={lastPayload} />}
                </>
              ) : (
                <div className="bg-card border border-border rounded-xl p-12 text-center">
                  <Calculator className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-slate-400 mb-2">Ingresa los datos</h4>
                  <p className="text-sm text-slate-500">Completa el formulario y presiona "Calcular Finiquito" para ver el resultado</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Scenarios Tab */}
        {activeTab === 'scenarios' && (
          <div className="space-y-4">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">{scenario.name}</h3>
                  <p className="text-slate-400 text-sm mb-4">{scenario.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                      <h4 className="text-emerald-400 font-medium mb-2">El trabajador recibe:</h4>
                      <ul className="text-sm text-slate-300 space-y-1">
                        {scenario.worker_receives?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {scenario.causes && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <h4 className="text-amber-400 font-medium mb-2">Causales:</h4>
                        <ul className="text-sm text-slate-300 space-y-1">
                          {scenario.causes.map((cause, i) => (
                            <li key={i}>• {cause}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {scenario.no_receives && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <h4 className="text-red-400 font-medium mb-2">No recibe:</h4>
                        <p className="text-sm text-slate-300">{scenario.no_receives}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Scale className="w-3 h-3" />
                      {scenario.legal_basis}
                    </span>
                    {scenario.challenge_deadline && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Plazo para impugnar: {scenario.challenge_deadline}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Info Tab */}
        {activeTab === 'info' && legalLimits && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Valores Legales Vigentes
              </h3>
              <div className="space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Sueldo Mínimo ({legalLimits.minimum_wage?.year} · {legalLimits.minimum_wage?.law})</p>
                  <p className="text-2xl font-bold text-white">{formatCLP(legalLimits.minimum_wage?.value)}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Tope Indemnización (90 UF, a UF de hoy)</p>
                  <p className="text-2xl font-bold text-white">{formatCLP(legalLimits.max_indemnification_base?.value)}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">UF del día {legalLimits.uf_reference?.date ? `(${legalLimits.uf_reference.date})` : ''}</p>
                  <p data-testid="labor-uf-value" className="text-2xl font-bold text-white">{formatCLP(legalLimits.uf_reference?.value)}</p>
                  <p className="text-xs text-slate-500">{legalLimits.uf_reference?.note}</p>
                </div>
                {legalLimits.utm && (
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">UTM ({legalLimits.utm.month})</p>
                    <p data-testid="labor-utm-value" className="text-2xl font-bold text-white">{formatCLP(legalLimits.utm.value)}</p>
                    <p className="text-xs text-slate-500">Fuente oficial: SII</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-400" />
                Límites y Topes
              </h3>
              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-blue-400 font-medium">Años de Servicio</p>
                  <p className="text-white">Máximo {legalLimits.years_cap?.value} años para cálculo de indemnización</p>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                  <p className="text-purple-400 font-medium">Vacaciones</p>
                  <p className="text-white">{legalLimits.vacation_days?.base} días base</p>
                  <p className="text-sm text-slate-400">{legalLimits.vacation_days?.progressive}</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <p className="text-amber-400 font-medium">Gratificación Legal</p>
                  <p className="text-white">Tope: {legalLimits.gratification_cap?.value} {legalLimits.gratification_cap?.unit}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LaborCalculatorPage;
